// Effekter: pust, lysbølger, gnister, svævende tal og ryst. Alle er forankret i kortet (km),
// så de følger med, hvis man panorerer. Effekter dækker aldrig information: de er korte og gennemsigtige.

import { BitmapText, Container, Sprite } from 'pixi.js';
import type { Atlas } from './atlas';
import type { P } from './geo';
import { C } from './palette';
import type { View } from './view';

export const NUM_FONT = 'FSNum';

interface Bit {
  s: Sprite | BitmapText;
  w: P;
  ox: number;
  oy: number;
  vx: number;
  vy: number;
  grav: number;
  life: number;
  max: number;
  s0: number;
  s1: number;
  a0: number;
  a1: number;
  spin: number;
  delay: number;
  /** Andel af levetiden med fuld synlighed, før den fader (tal skal kunne læses). */
  hold: number;
  /** Kaldes, når effekten er færdig (raketten springer ud). */
  done?: (at: P, ox: number, oy: number) => void;
  /** Et pengetal (kr, med fortegn). Tal fra samme øjeblik lægges sammen til ét. */
  kr?: number;
}

/** Fyrværkeriets farver: brandets glade farver. Aldrig rød, den betyder udfald. */
const FIREWORK = [C.yellow, C.green, C.orange, C.turquoise, C.white];

export class Fx {
  readonly c = new Container();
  private bits: Bit[] = [];
  private shakeT = 0;
  private shakeAmp = 0;
  shakeEnabled = true;
  /** Højst så mange partikler ad gangen (kvalitetstrinnet sætter det). Tal tæller ikke med. */
  budget = 600;

  constructor(private atlas: Atlas) {}

  /** Antal levende effekter (til testkrogen og budgettet). */
  get count(): number {
    return this.bits.length;
  }

  private add(s: Sprite | BitmapText, w: P, b: Partial<Bit>): void {
    // Over budget: dropper vi pynten. Tal skal altid frem, de er information.
    if (!(s instanceof BitmapText) && this.bits.length >= this.budget) {
      s.destroy();
      return;
    }
    const bit: Bit = { s, w, ox: 0, oy: 0, vx: 0, vy: 0, grav: 0, life: 0, max: 600, s0: 1, s1: 1, a0: 1, a1: 0, spin: 0, delay: 0, hold: 0, ...b };
    s.visible = bit.delay <= 0;
    this.c.addChild(s);
    this.bits.push(bit);
  }

  puff(w: P, n = 7, tint: number = C.white, size = 1): void {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.random() * 0.6;
      const sp = 40 + Math.random() * 50;
      const s = this.atlas.sprite('puff');
      s.tint = tint;
      this.add(s, w, { vx: Math.cos(a) * sp, vy: Math.sin(a) * sp * 0.6 - 20, max: 520 + Math.random() * 200, s0: 0.6 * size, s1: 1.4 * size, a0: 0.95, a1: 0 });
    }
  }

  smoke(w: P, ox: number, oy: number, k: number): void {
    const s = this.atlas.sprite('puff');
    s.tint = C.smoke;
    this.add(s, w, { ox, oy, vx: 6 + Math.random() * 6, vy: -16 - Math.random() * 8, max: 2200, s0: 0.35 * k, s1: 1.1 * k, a0: 0.75, a1: 0 });
  }

  /** Lysbølge: to ringe, der udvider sig, når en by får strøm. */
  ring(w: P, color: number = C.yellow, size = 1, delay = 0, alpha = 0.9, count = 2): void {
    for (let i = 0; i < count; i++) {
      const s = this.atlas.sprite('ring');
      s.tint = color;
      this.add(s, w, { max: 750, s0: 0.25 * size, s1: 1.9 * size, a0: alpha, a1: 0, delay: delay + i * 160 });
    }
  }

  sparks(w: P, n = 6, color: number = C.yellow): void {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 60 + Math.random() * 80;
      const s = this.atlas.sprite('spark');
      s.tint = color;
      this.add(s, w, { vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 30, grav: 160, max: 520, s0: 0.9, s1: 0.2, a0: 1, a1: 0.2, spin: 6 });
    }
  }

  text(w: P, str: string, color: number = C.white, oy = -30): void {
    // IGN-løkke 2, runde 3: "+4 kr-5 kr" stod oven i hinanden, når en ledning kostede og en by betalte
    // i samme tick. Pengetal fra samme øjeblik og sted lægges sammen til ét tal.
    const m = /^([+-])(\d+) kr$/.exec(str);
    if (m) {
      const kr = (m[1] === '-' ? -1 : 1) * Number(m[2]);
      const twin = this.bits.find((b) => b.kr !== undefined && b.life < 300 && Math.hypot(b.w.x - w.x, b.w.y - w.y) < 160);
      if (twin && twin.s instanceof BitmapText) {
        twin.kr = (twin.kr ?? 0) + kr;
        const sum = twin.kr;
        twin.s.text = sum === 0 ? '0 kr' : `${sum > 0 ? '+' : '-'}${Math.abs(sum)} kr`;
        twin.s.tint = sum > 0 ? C.yellow : C.white;
        return;
      }
    }
    // Tal må aldrig klumpe: højst fire ad gangen, og nye venter på tur og skiftes til hver side.
    const live = this.bits.filter((b) => b.s instanceof BitmapText);
    if (live.length >= 4) return;
    const queued = live.filter((b) => b.delay > 0 || b.life < 420).length;
    const t = new BitmapText({ text: str, style: { fontFamily: NUM_FONT, fontSize: 22 } });
    t.anchor.set(0.5);
    t.tint = color;
    // Tal fra nabopunkter skifter side og trappes op, så to byer tæt på hinanden ikke skriver oven i hinanden.
    const near = live.filter((b) => b.life < b.max * 0.8 && Math.hypot(b.w.x + b.ox - w.x, b.w.y - w.y) < 70).length;
    const ox = near === 0 ? 0 : near % 2 === 1 ? 34 : -34;
    const lift = near > 1 ? 22 : 0;
    this.add(t, w, { ox, oy: oy - lift, vy: -30, max: 1400, s0: 0.75, s1: 1, a0: 1, a1: 0, hold: 0.6, delay: queued * 420, ...(m ? { kr: (m[1] === '-' ? -1 : 1) * Number(m[2]) } : {}) });
  }

  bolt(w: P, oy: number): void {
    const s = this.atlas.sprite('bolt');
    this.add(s, w, { oy, max: 380, s0: 1.6, s1: 1.2, a0: 1, a1: 0 });
  }

  /** Raket fra punktet: stiger, springer ud i en stjerne af gnister. Kun i Pixi-verdenen, aldrig i UI. */
  firework(w: P, delay = 0, color: number = FIREWORK[Math.floor(Math.random() * FIREWORK.length)] ?? C.yellow): void {
    const s = this.atlas.sprite('dot');
    s.tint = color;
    const vx = (Math.random() - 0.5) * 50;
    this.add(s, w, {
      vx,
      vy: -240 - Math.random() * 70,
      grav: 300,
      max: 640 + Math.random() * 160,
      s0: 0.6,
      s1: 0.4,
      a0: 1,
      a1: 0.8,
      delay,
      done: (at, ox, oy) => this.burst(at, ox, oy, color),
    });
  }

  private burst(w: P, ox: number, oy: number, color: number): void {
    const n = 16;
    const g = this.atlas.sprite('glow');
    g.tint = color;
    this.add(g, w, { ox, oy, max: 420, s0: 0.5, s1: 1.6, a0: 0.7, a1: 0 });
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.random() * 0.2;
      const sp = 70 + Math.random() * 40;
      const s = this.atlas.sprite(i % 3 === 0 ? 'spark' : 'dot');
      s.tint = i % 4 === 0 ? C.white : color;
      this.add(s, w, { ox, oy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, grav: 60, max: 800 + Math.random() * 300, s0: 0.55, s1: 0.15, a0: 1, a1: 0, spin: 3 });
    }
  }

  /** Skærmrektangler for svævende tal, så navneskilte kan gå uden om dem. */
  textRects(v: View): { x: number; y: number; w: number; h: number }[] {
    const out: { x: number; y: number; w: number; h: number }[] = [];
    for (const b of this.bits) {
      if (!(b.s instanceof BitmapText) || !b.s.visible) continue;
      const base = v.cam.toScreen(b.w);
      const x = base.x + b.ox * v.k;
      const y = base.y + b.oy * v.k;
      const w = b.s.width;
      const h = b.s.height;
      out.push({ x: x - w / 2, y: y - h / 2, w, h });
    }
    return out;
  }

  shake(amp: number): void {
    if (!this.shakeEnabled) return;
    this.shakeAmp = Math.max(this.shakeAmp, amp);
    this.shakeT = 380;
  }

  /** Opdatér alle effekter og returnér rystets forskydning. */
  update(v: View): P {
    const dt = v.dt;
    const k = v.k;
    const keep: Bit[] = [];
    const list = this.bits;
    // Effekter, der føder nye (raketten), lægger dem i en frisk liste undervejs.
    this.bits = [];
    for (const b of list) {
      if (b.delay > 0) {
        b.delay -= dt;
        if (b.delay > 0) {
          keep.push(b);
          continue;
        }
        b.s.visible = true;
      }
      b.life += dt;
      const t = Math.min(1, b.life / b.max);
      const sec = dt / 1000;
      b.vy += b.grav * sec;
      b.ox += b.vx * sec;
      b.oy += b.vy * sec;
      const base = v.cam.toScreen(b.w);
      b.s.position.set(base.x + b.ox * k, base.y + b.oy * k);
      const ease = 1 - (1 - t) * (1 - t);
      b.s.scale.set((b.s0 + (b.s1 - b.s0) * ease) * k);
      const ta = b.hold > 0 ? Math.max(0, (t - b.hold) / (1 - b.hold)) : t;
      b.s.alpha = b.a0 + (b.a1 - b.a0) * ta;
      b.s.rotation += b.spin * sec;
      if (t >= 1) {
        b.done?.(b.w, b.ox, b.oy);
        b.s.destroy();
      } else {
        keep.push(b);
      }
    }
    this.bits = keep.concat(this.bits);
    if (this.shakeT > 0) {
      this.shakeT -= dt;
      const a = this.shakeAmp * Math.max(0, this.shakeT / 380);
      if (this.shakeT <= 0) this.shakeAmp = 0;
      return { x: (Math.random() - 0.5) * 2 * a, y: (Math.random() - 0.5) * 2 * a };
    }
    return { x: 0, y: 0 };
  }

  clear(): void {
    for (const b of this.bits) b.s.destroy();
    this.bits = [];
  }
}
